import React, { CSSProperties, PropsWithChildren, ReactNode, useCallback, useEffect, useRef } from 'react';
import cls from 'classnames';
import { useToggle } from '@cutwise/react-utils';

import styles from './AnimatedDropdown.module.css';

interface AnimatedDropdownProps {
  className?: string;
  label?: ReactNode;
  'aria-label'?: string;
}

const ANIMATION_DURATION = 300;

export default function AnimatedDropdown(props: PropsWithChildren<AnimatedDropdownProps>) {
  const { className, label, 'aria-label': ariaLabel = 'Toggle dropdown', children } = props;

  const contentRef = useRef<HTMLDivElement>(null);

  const [opened, , close, toggle] = useToggle();
  const [isAnimating, startAnimating, stopAnimating] = useToggle();

  const getContentHeight = useCallback(() => {
    const element = contentRef.current;

    if (!element) {
      return 0;
    }

    const { top } = element.getBoundingClientRect();
    const gapFromBottom = 25; // padding from bottom edge of the screen in pixels

    return document.body.clientHeight - top - gapFromBottom;
  }, []);

  const onClickOutsideHandler = useCallback((e: MouseEvent) => {
    e.stopPropagation();

    close();
    startAnimating();
  }, []);

  const onOpenHandler = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();

    toggle();
    startAnimating();
  }, []);

  useEffect(() => {
    if (opened) {
      document.addEventListener('click', onClickOutsideHandler);
    } else {
      document.removeEventListener('click', onClickOutsideHandler);
    }

    return () => {
      document.removeEventListener('click', onClickOutsideHandler);
    };
  }, [opened]);

  useEffect(() => {
    let timeout: SetTimeout | undefined = undefined;

    if (isAnimating) {
      clearTimeout(timeout);
      timeout = setTimeout(() => stopAnimating(), ANIMATION_DURATION);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [isAnimating]);

  return (
    <div className={cls(styles.animatedDropdown, className)}>
      <div
        aria-label={ariaLabel}
        className={cls(styles.label, { [styles.opened]: opened })}
        onClick={onOpenHandler}
        role="button"
        tabIndex={0}
      >
        <div className={styles.labelText}>{label}</div>

        <span className={cls(styles.triangle, { [styles.opened]: opened })} />
      </div>

      <div
        className={cls(styles.content, { [styles.closed]: !opened, [styles.animating]: isAnimating })}
        style={
          {
            maxHeight: getContentHeight(),
            '--duration': `${ANIMATION_DURATION}ms`,
          } as CSSProperties
        }
      >
        <div ref={contentRef} aria-label="Dropdown content" className={styles.menuContainer} onClick={close}>
          {children}
        </div>
      </div>
    </div>
  );
}
